import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Api from '../../../../../services/Api';
import BasePage from '../../../BasePage/BasePage';
import ImageCarousel from '../../../../atoms/ImageCarousel/ImageCarousel';
import Grid from '../../../../atoms/Grid/Grid';
import InputControl from '../../../../atoms/InputControl/InputControl';
import TagsInput from '../../../../atoms/TagsInput/TagsInput';
import DynastySelector from '../../../../atoms/DynastySelector/DynastySelector';
import CitySelector from '../../../../atoms/CitySelector/CItySelector';
import CitationEditor from '../../../../molecules/CitationEditor/CitationEditor';
import Button from '../../../../atoms/Button/Button';
import Icon from '../../../../atoms/Icon/Icon';
import {
  addProductSuccessAction,
  fetchProductsInitAction,
  getProductSuccessAction,
  updateSelectedProductAction,
} from '../../../../../actions/instances/products';
import BuildingsSelector from '../../../../atoms/BuildingsSelector/BuildingsSelector';
import extractErrors from '../../../../../helpers/errors';
import ProductsTypeSelector from '../../../../atoms/ProductTypeSelector/ProductTypeSelector';
import InstanceDocumentEditor from '../../../../Organisms/InstanceDocumentEditor/InstanceDocumentEditor';
import CharacteristicsSelector from '../../../../atoms/CharacteristicsSelector/CharacteristicsSelector';
import './CreateProduct.scss';

const CreateProduct = () => {
  const { singleProduct } = useSelector(state => state.products);
  const [product, setProduct] = useState(singleProduct);
  useEffect(() => {
    if (singleProduct) {
      Api.get(`instances/building-products/${singleProduct.id}`).then(response => setProduct(response.data));
    }
  }, []);
  let mainImage;
  let restOfImages = [];
  if (product && product.images) {
    if (product.images.length > 0) {
      [mainImage] = product.images;
      restOfImages = product.images.slice(0, 1);
    }
  }
  const [imagesIds, setImagesIds] = useState(restOfImages);
  const [mainImageId, setMainImageId] = useState(mainImage);
  const [name, setName] = useState(product ? product.name : undefined);
  const [creatorName, setCreatorName] = useState(product ? product.creator_name : undefined);
  const [startDate, setStartDate] = useState(product ? product.start_date : undefined);
  const [endDate, setEndDate] = useState(product ? product.end_date : undefined);
  const [productTypeId, setProductTypeId] = useState(product ? product.product_type_id : undefined);
  const [dynastyCode, setDynastyCode] = useState(product ? product.dynasty.code : undefined);
  const [dynastyId, setDynastyId] = useState(product ? product.dynasty_id : undefined);
  const [cityId, setCityId] = useState(product ? product.city.id : undefined);
  const [cityCode, setCityCode] = useState(product ? product.city.code : undefined);
  const [resourcesIds, setResourcesIds] = useState(
    product && product.citation ? product.citation.map(c => c.id) : undefined,
  );
  const [errors, setErrors] = useState(undefined);
  const [tagsIds, setTagsIds] = useState(
    product && product.tags ? product.tags.map(t => t.id) : undefined,
  );
  const [buildingsIds, setBuildingsIds] = useState(
    product && product.buildings ? product.buildings.map(x => x.id) : undefined,
  );
  const [characteristicsIds, setCharacteristicsIds] = useState(
    product && product.characteristics
      ? product.characteristics.map(x => x.id) : undefined,
  );
  const [documentsIds, setDocumentsIds] = useState(
    product ? product.documents.map(doc => doc.id) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [identifier, setIdentifier] = useState(
    product ? `${product.city.code}-${product.dynasty.code}-${product.code}` : undefined,
  );
  useEffect(() => {
    let combinedCode = '';
    if (cityCode) {
      combinedCode += `${cityCode}-`;
    }
    if (dynastyCode) {
      combinedCode += `${dynastyCode}-`;
    }
    combinedCode += 'BPNXXXX';
    setIdentifier(combinedCode);
  }, [cityCode, dynastyCode]);
  useEffect(() => {
    if (product && product.buildings) {
      setBuildingsIds(product.buildings.map(x => x.id));
    }
    if (product && product.characteristics) {
      setCharacteristicsIds(product.characteristics.map(x => x.id));
    }
  }, [product]);
  const dispatch = useDispatch();

  const history = useHistory();
  const handleSave = () => {
    if (!name) {
      setErrors('Product name is required');
      return;
    }
    if (!productTypeId) {
      setErrors('You must select a type for the product');
      return;
    }
    if (!cityId) {
      setErrors('You must select a region for the product');
      return;
    }
    if (!dynastyId) {
      setErrors('You must select a dynasty for the product');
      return;
    }
    setIsLoading(true);
    const data = {
      name,
      code_suffix: 'BPN',
      city_id: cityId,
      dynasty_id: dynastyId,
      images_ids: [mainImageId, ...imagesIds].filter(img => img !== undefined),
      product_type_id: productTypeId,
      tags_ids: tagsIds,
      buildings_ids: buildingsIds,
      characteristics_ids: characteristicsIds,
      resources_ids: resourcesIds,
      documents_ids: documentsIds,
      creator_name: creatorName,
      start_date: startDate,
      end_date: endDate,
    };
    const callback = (response) => {
      if (response.status === 201) {
        dispatch(addProductSuccessAction(response.data));
        history.goBack();
      } else if (response.status === 200) {
        dispatch(updateSelectedProductAction(response.data));
      } else {
        const errs = extractErrors(response.data);
        setErrors([errs[0]]);
        setIsLoading(false);
        return;
      }
      dispatch(fetchProductsInitAction());
      setTimeout(() => {
        setIsLoading(false);
        history.goBack();
      }, 500);
    };
    if (product) {
      Api.patch(`instances/building-products/${product.id}`, data).then(callback);
    } else {
      Api.post('instances/building-products', data).then(callback);
    }
  };
  return (
    <BasePage>
      <div className="create-component-container">
        <h2 className="col-md-12">
          {product ? `Edit ${product.name} Product` : 'Create New Product'}
        </h2>
        <br />
        <Grid>
          <div className="col-md-3 left-column">
            <ImageCarousel
              initialImages={product ? product.images : []}
              additionalImagesAddedCallback={images => setImagesIds(images)}
              mainImageAddedCallback={imageId => setMainImageId(imageId)}
            />
            <hr />
            <div className="col-md-12 dynasty-section">
              <InstanceDocumentEditor
                initialDocuments={product ? product.documents : []}
                onFilesChanged={(files) => {
                  setDocumentsIds(files.map(f => f.id));
                }}
              />
            </div>
            <div className="tags-container">
              <label className="tag-label">Product Tags</label>
              <TagsInput tags={tagsIds} placeholder="Start typing..." onChange={tags => setTagsIds(tags)} />
            </div>
          </div>
          <div className="col-md-9 content-column">
            <Grid>
              <div className="col-md-12">
                <label>Product Type</label>
                <ProductsTypeSelector
                  selectedId={productTypeId}
                  onChanged={(dynasty) => {
                    setProductTypeId(dynasty.value);
                  }}
                />
              </div>
              <div className="col-md-12">
                <label>Product Dynasty</label>
                <DynastySelector
                  selectedId={dynastyId}
                  onChanged={(dynasty) => {
                    setDynastyId(dynasty.value);
                    setDynastyCode(dynasty.code);
                  }}
                />
              </div>
              <div className="col-md-12">
                <label>Location</label>
                <CitySelector
                  selectedId={cityId}
                  onChanged={(city) => {
                    setCityId(city.value);
                    setCityCode(city.code);
                  }}
                />
              </div>
              <div className="col-md-12">
                <InputControl label="Product name" value={name} type="text" onValueChanged={value => setName(value)} placeholder="Name..." />
                <small>{identifier}</small>
              </div>
              <div className="col-md-12">
                <InputControl label="Creator name" value={creatorName} type="text" onValueChanged={value => setCreatorName(value)} placeholder="Creator Name..." />
              </div>
              <div className="col-md-6 mt-3">
                <InputControl label="Start Date" value={startDate} type="text" onValueChanged={value => setStartDate(value)} placeholder="Start Date..." />
              </div>
              <div className="col-md-6 mt-3">
                <InputControl label="End Date" value={endDate} type="text" onValueChanged={value => setEndDate(value)} placeholder="End Date..." />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Product Buildings</label>
                <BuildingsSelector
                  cityId={cityId}
                  dynastyId={dynastyId}
                  selectedIds={buildingsIds}
                  onChanged={(values) => {
                    const ids = values.map(val => parseInt(val.value, 10));
                    setBuildingsIds(ids);
                  }}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Product Characteristics</label>
                <CharacteristicsSelector
                  onChanged={(values) => {
                    const ids = values.map(val => parseInt(val.value, 10));
                    setCharacteristicsIds(ids);
                  }}
                  selectedIds={characteristicsIds}
                  isMulti
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <CitationEditor
                  onChange={values => setResourcesIds(values)}
                  initialCitations={product ? product.citation : []}
                />
              </div>
              {errors && (
                <div className="col-md-12 errors-container">
                  <span className="error">{errors}</span>
                </div>
              )}
              <div className="col-md-12 actions">
                <Button
                  onInvoked={() => {
                    dispatch(getProductSuccessAction(undefined));
                    history.goBack();
                  }}
                >
                  <Icon icon="times" />
                  <span>Cancel</span>
                </Button>
                <Button onInvoked={handleSave} isLoading={isLoading}>
                  <Icon icon="save" />
                  <span>Save</span>
                </Button>
              </div>
            </Grid>
          </div>
        </Grid>
      </div>
    </BasePage>
  );
};

export default CreateProduct;
